import _ from 'lodash';
import Vue from 'vue';
import Segment from '../../Segment';
import CredentialsApi from '../../api/CredentialsApi';
import sourcesFieldsList from '../../constants/sourcesFields';
import serial from '../../utils/serial';

const state = {
  credentialsList: [],
  savingInProcess: false,
  credsToAccounts: [],
  sourceType: null,
  runAfterSave: false,
  loadingCreds: [],
};

const getters = {
  credsBySourceType: state => (state.sourceType ? _.filter(state.credentialsList, { type: state.sourceType }) : state.credentialsList),
  // eslint-disable-next-line max-len
  discountPendingCredentialsList: (state, getters) => _.filter(getters.credsBySourceType, creds => creds.status === 'pending' && creds.sourceName === 'דיסקונט ישיר'),
  invalidCredentialsList: (state, getters) => _.filter(getters.credsBySourceType, { status: 'passwordInvalid' }),
  validCredentialsList: (state, getters) => _.filter(getters.credsBySourceType, { status: 'valid' }),
  unvalidatedCredentialsList: (state, getters) => _.filter(getters.credsBySourceType, { status: 'unvalidated' }),
  newCredentialsList: (state, getters) => _.filter(getters.credsBySourceType, { status: 'new' }),
  invalidCredentials: (state, getters) => getters.invalidCredentialsList.length,
  hasCredentials: (state, getters) => getters.credsBySourceType.length > 0,
  hasValidationErrors: (state, getters) => _.some(getters.credsBySourceType, creds => creds.updateError || creds.validations.nameError),
};

async function checkCredentials(sourceName, credentials, safeId, credentialsId, renew) {
  const result = await _checkCredentials(sourceName, credentials, safeId, credentialsId, renew);
  await CredentialsApi.logCredsCheck(credentialsId, result.isValid, result.reason);
  return result;
}

async function _checkCredentials(sourceName, credentials, safeId, credentialsId, renew) {
  const defaultResult = { isValid: true, reason: 'DefaultClientResult' };

  try {
    const result = await CredentialsApi.checkCredentials({ sourceName, credentials, safeId, credentialsId, renew });
    return { isValid: result.success, reason: result.reason };
  } catch (e) {
    return defaultResult;
  }
}

function _extractCredentials(credentialsFields) {
  return _.chain(credentialsFields)
    .mapValues(val => val.value)
    .omitBy(val => !val)
    .mapValues(val => val.trim())
    .value();
}

async function _tryUpdatingCreds(commit, sourceName, payload) {
  try {
    await CredentialsApi.updateCredentials(payload);
    Segment.trackUserGot('CredentialsSaveSuccess', { sourceName });
    return { success: true };
  } catch (e) {
    if (e.response && e.response.status === 400) {
      commit('setNameError', { credentialsId: payload.credentialsId, error: 'כבר השתמשת בשם הזה' });
      Segment.trackUserGot('CredentialsSaveFailed', { sourceName, reason: 'SourceNameAlreadyExists' });
      return { success: false };
    }
    Segment.trackUserGot('CredentialsSaveFailed', { sourceName, reason: 'UnexpectedException' });
    commit('setUpdateError', { credentialsId: payload.credentialsId });
    return { success: false };
  }
}

async function _trySavingCreds(commit, oldCredentialsId, sourceName, credentials, name) {
  try {
    const { credentialsId } = await CredentialsApi.saveCredentials({ sourceName, credentials, name });
    commit('updateCredentialsId', { oldCredentialsId, newCredentialsId: credentialsId });
    Segment.trackUserGot('CredentialsSaveSuccess', { sourceName });
    return { success: true };
  } catch (e) {
    if (e.response && e.response.status === 400) {
      commit('setNameError', { oldCredentialsId, error: 'כבר השתמשת בשם הזה' });
      Segment.trackUserGot('CredentialsSaveFailed', { sourceName, reason: 'SourceNameAlreadyExists' });
      return { success: false };
    }
    Segment.trackUserGot('CredentialsSaveFailed', { sourceName, reason: 'UnexpectedException' });
    commit('setUpdateError', { oldCredentialsId });
    return { success: false };
  }
}

const actions = {
  async createCredentialsForSources({ dispatch }, { sourceNames }) {
    await serial.serialFor(sourceNames, async sourceName => {
      await dispatch('createCredentials', { sourceName });
    });
  },
  async createCredentials({ commit }, { sourceName }) {
    const { credentials } = await CredentialsApi.createCredentials({ sourceName });
    commit('addCredentialsToCredentialsList', credentials);
    return credentials.credentialsId;
  },
  async deleteCredentials({ commit }, { credentialsId }) {
    await CredentialsApi.deleteCredentials({ credentialsId });
    commit('removeCredentialsId', credentialsId);
  },
  async fetchCredsToAccounts({ commit }) {
    const credsToAccounts = await CredentialsApi.fetchCredsToAccounts();
    commit('setCredsToAccounts', credsToAccounts);
  },
  async fetchCredentials({ commit, state, dispatch }) {
    const rawCredentialsList = await CredentialsApi.fetchCredentials();
    await dispatch('fetchCredsToAccounts');
    const credentialsList = _.map(rawCredentialsList, _.partial(convertRawCredentials, state.credsToAccounts));
    commit('setCredentialsList', credentialsList);
  },
  updateCredentialsStatus({ commit, state }, { source, status }) {
    commit('setSourceStatus', { credentialsId: source.credentialsId, status });
    commit('sortCredentials');
  },
  async updateCredentials({ commit, state }, { source, renew = false }) {
    const credentialsObject = _.find(state.credentialsList, { credentialsId: source.credentialsId });
    const { fields: credentialsFields, safeId, name, sourceName, credentialsId } = credentialsObject;
    const credentials = _extractCredentials(credentialsFields);
    commit('resetUpdateError', { credentialsId });
    commit('setNameError', { credentialsId, error: '' });

    if (source.createOnSave) {
      return await _trySavingCreds(commit, credentialsId, sourceName, credentials, name);
    }
    const checkCredsResult = await checkCredentials(sourceName, credentials, safeId, credentialsId, renew);
    commit('setCredsValidation', { credentialsId, checkCredsResult });
    if (checkCredsResult.isValid) {
      return await _tryUpdatingCreds(commit, sourceName, { credentialsId, credentials, name });
    }
    Segment.trackUserGot('CredentialsSaveFailed', { sourceName, reason: 'InvalidCredentials' });
    return { success: false };
  },
  async updatePendingCredentials({ commit, state }, { source, renew = false }) {
    const { credentialsId, name, sourceName } = source;
    const { fields: credentialsFields } = _.find(state.credentialsList, { credentialsId });
    const credentials = renew ? _extractCredentials(credentialsFields) : {};

    return await _tryUpdatingCreds(commit, sourceName, { credentialsId, credentials, name, status: 'pending' });
  },

  async updateCredentialsField({ commit }, { credentialsId, fieldKey, value }) {
    commit('setCredentialsField', { credentialsId, fieldKey, value });
  },
  async setName({ commit }, { credentialsId, name }) {
    commit('setCredentialsName', { credentialsId, name });
  },
  async setNameError({ commit }, { credentialsId, error }) {
    commit('setNameError', { credentialsId, error });
  },
  async setSavingInProcess({ commit }, savingInProcess) {
    commit('setSavingInProcess', { savingInProcess });
  },
  async setDiscountCredentialsType({ commit }, { credentialsId, credentialsType }) {
    commit('setDiscountCredentialsType', { credentialsId, credentialsType });
  },
};

// TODO: a whole bunch of these mutations do the same thing of can at least be generalized to do something similar
// mutations
const mutations = {
  setSourceType(state, sourceType) {
    state.sourceType = sourceType; // eslint-disable-line
  },
  setRunAfterSave(state, runAfterSave) {
    state.runAfterSave = runAfterSave; // eslint-disable-line
  },
  setCredentialsList(state, credentialsList) {
    state.credentialsList = credentialsList; // eslint-disable-line
  },
  setCredsToAccounts(state, credsToAccounts) {
    state.credsToAccounts = credsToAccounts; // eslint-disable-line
  },
  setLoadingCreds(state, loadingCreds) {
    state.loadingCreds = loadingCreds; // eslint-disable-line
  },
  addCredentialsToCredentialsList(state, source) {
    state.credentialsList.push(convertRawCredentials([], source));
  },
  removeCredentialsId(state, credentialsId) {
    const index = _.findIndex(state.credentialsList, { credentialsId });
    state.credentialsList.splice(index, 1);
  },
  setCredentialsName(state, { credentialsId, name }) {
    const account = _.find(state.credentialsList, { credentialsId });
    Vue.set(account, 'name', name);
  },
  setSourceStatus(state, { credentialsId, status }) {
    const credentials = _.find(state.credentialsList, { credentialsId });
    Vue.set(credentials, 'status', status);
  },
  setCredentialsField(state, { credentialsId, fieldKey, value }) {
    const account = _.find(state.credentialsList, { credentialsId });
    Vue.set(account.fields[fieldKey], 'value', value);
  },
  updateCredentialsId(state, { oldCredentialsId, newCredentialsId }) {
    const credentials = _.find(state.credentialsList, { credentialsId: oldCredentialsId });
    Vue.set(credentials, 'credentialsId', newCredentialsId);
  },
  setSavingInProcess(state, { savingInProcess }) {
    state.savingInProcess = savingInProcess; // eslint-disable-line
  },
  setNameError(state, { credentialsId, error }) {
    const credentials = _.find(state.credentialsList, { credentialsId });
    Vue.set(credentials.validations, 'nameError', error);
  },
  setUpdateError(state, { credentialsId }) {
    const credentials = _.find(state.credentialsList, { credentialsId });
    Vue.set(credentials, 'updateError', true);
  },
  resetUpdateError(state, { credentialsId }) {
    const credentials = _.find(state.credentialsList, { credentialsId });
    Vue.set(credentials, 'updateError', false);
  },
  setCredsValidation(state, { credentialsId, checkCredsResult }) {
    const credentials = _.find(state.credentialsList, { credentialsId });
    Vue.set(credentials.validations, 'checkCredentials', checkCredsResult);
  },
  setDiscountCredentialsType(state, { credentialsId, credentialsType }) {
    const credentials = _.find(state.credentialsList, { credentialsId });
    Vue.set(credentials, 'discountCredentialsType', credentialsType);
  },
  sortCredentials(state) {
    state.credentialsList.sort((a, b) => {
      if (a.status === 'valid' && b.status === 'valid') {
        return 0;
      }
      if (a.status === 'valid') {
        return -1;
      }
      if (b.status === 'valid') {
        return 1;
      }
      return 0;
    });
  },
};

function convertRawCredentials(credsToAccounts, rawCredentials) {
  const credToAccounts = _.find(credsToAccounts, credToAccounts => credToAccounts.credentialsId === rawCredentials.credentialsId);
  const sourceFieldsObject = getSourceFieldsObject(rawCredentials.sourceName);
  const key = rawCredentials.credentialsId;
  return _.assign({}, rawCredentials, sourceFieldsObject, key, { accounts: credToAccounts && credToAccounts.accounts });
}

function getSourceFieldsObject(sourceName) {
  const source = _.find(sourcesFieldsList.allSourceFields, { sourceName });
  const fieldsWithValue = _.mapValues(source.fields, fieldValue => _.assign({ value: undefined }, fieldValue));
  return _.assign(
    { validations: { nameError: '', checkCredentials: { isValid: true, reason: 'DefaultClientResult' } } },
    { updateError: false },
    source,
    { fields: fieldsWithValue },
  );
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
